export const DATA_EVENT_TRACKER_ATTR = "data-event-tracker";

export type TTrackerObject = {
  params?: Record<string, unknown>; // 埋点参数
};

export type TTrackerInput = string | TTrackerObject;

export type TTrackerValidation = {
  ok: boolean;
  value?: TTrackerObject;
  error?: string;
};

export type TTrackerLogger = {
  error: (msg: string, extra?: unknown) => void;
  warn?: (msg: string, extra?: unknown) => void;
};

export const defaultLogger: TTrackerLogger = {
  error: (msg, extra) => console.error(msg, extra),
  warn: (msg, extra) => console.warn(msg, extra),
};

/**
 * 验证埋点追踪数据 (TTrackerInput) 的有效性和格式。
 * * @param input 原始埋点数据，可以是对象或 JSON 字符串。
 * @returns 验证结果，包含 ok 状态和错误信息或有效值。
 */
export function validateTracker(
  input?: TTrackerInput | undefined,
): TTrackerValidation {
  if (!input) return { ok: true, value: { params: {} } };
  try {
    const obj =
      typeof input === "string" ? (JSON.parse(input) as unknown) : input;
    if (!obj || typeof obj !== "object")
      return { ok: false, error: "tracker must be object" };

    const { params } = obj as TTrackerObject;
    if (params && (typeof params !== "object" || Array.isArray(params))) {
      return { ok: false, error: "params must be plain object" };
    }
    return { ok: true, value: { params } };
  } catch {
    return { ok: false, error: "invalid JSON string" };
  }
}

/**
 * 验证并序列化埋点追踪数据，以便嵌入到 DOM 属性中。
 * @param input 原始埋点数据对象。
 * @param logger 日志记录工具。
 * @returns 序列化后的 JSON 字符串，或 null（如果验证或转换失败）。
 */
export function normalizeTrackerToAttr(
  input?: TTrackerInput,
  logger: TTrackerLogger = defaultLogger,
): string | null {
  const v = validateTracker(input);
  if (!v.ok) {
    logger.error("[tracker] invalid payload", v.error);
    return null;
  }
  try {
    return JSON.stringify(v.value!);
  } catch (e) {
    logger.error("[tracker] stringify failed", e);
    return null;
  }
}

/**
 * 将结构化的埋点数据转换为 DOM 属性键值对。
 * 返回 { [attributeName]: '序列化字符串' } 或 {}。
 */
export function buildTrackerProps(
  input?: TTrackerInput,
  logger: TTrackerLogger = defaultLogger,
  attributeName = DATA_EVENT_TRACKER_ATTR,
): Record<string, string> {
  const serializedData = normalizeTrackerToAttr(input, logger);
  return { [attributeName]: serializedData ?? "" };
}
